// lib/coupons/engine.ts
//
// Motor PURO del sistema de cupones (spec docs/superpowers/specs/2026-07-30-cupones-design.md §4).
// Sin DB, sin IO: matemática y reglas. Los rieles (PR-2/PR-3) lo importan.
//
// INVARIANTE de redondeo: espejo literal de lib/utils/pricing.ts (§13.2 de la spec):
// no hay helper compartido; la norma es Math.round(x*100)/100 inline, y la
// SECUENCIA es computar → topar → redondear (pricing.ts:71-81).

export type CouponDiscountType = "percent" | "fixed"

/**
 * Matemática de un cupón SIN ámbito — la de v1, que se computa y se topa contra
 * el total de la orden (`rawSubtotal`).
 *
 * Los tres `?: never` no son decoración: `ScopedCouponMath` es estructuralmente
 * un SUPERCONJUNTO de esta interfaz, así que sin ellos TypeScript aceptaría
 * pasar un cupón con ámbito a `computeCouponDiscount` / `applyDiscountStack` y
 * el descuento se calcularía sobre la ORDEN ENTERA. Es exactamente el bug que
 * la spec nombra en D-17 ("pasar solo `rawSubtotal` es el bug de 'descuento
 * sobre la orden entera'"), y `applyDiscountStack` es camino VIVO desde
 * `lib/utils/pricing.ts`. Con `?: never` ese error es un TS2322 en vez de un
 * cobro de menos.
 *
 * La composición correcta la fija el describe "ámbito × stacking (spec §6.3)"
 * de `__tests__/engine.scope.test.ts`: primero `computeScopedCouponDiscount`, y
 * su `amount` entra al stacking como `{ discount_type: "fixed", discount_value: amount }`.
 */
export interface CouponMath {
  discount_type: CouponDiscountType
  discount_value: number
  scope?: never
  max_scope_units?: never
  max_discount_amount?: never
}

export type CouponRejectReason =
  | "feature_disabled"
  | "invalid_format"
  | "not_found"
  | "inactive"
  | "not_started"
  | "expired"
  | "exhausted"
  | "min_subtotal"
  | "already_used"
  | "already_consumed"
  | "scope_not_matched"
  | "assigned_mismatch"
  // A1b (R2, TABC): el carrito es TODO alcohol ⇒ la base del cupón es 0. Lo
  // devuelven las rutas pre-cobro (/api/coupons/apply, /validate,
  // /online-orders/coupon-preview y /session); /api/orders nunca lo usa (honra
  // a $0). No lo produce el motor: lo decide cada ruta con la base.
  | "alcohol_only"

export const COUPON_CODE_RE = /^[A-Z0-9]{4,20}$/

/** Trim + mayúsculas; null si el resultado no cumple ^[A-Z0-9]{4,20}$ (mismo CHECK del schema). */
export function normalizeCouponCode(raw: string): string | null {
  const code = raw.trim().toUpperCase()
  return COUPON_CODE_RE.test(code) ? code : null
}

/**
 * Descuento del cupón sobre el subtotal de items en base card (rawSubtotal).
 * Secuencia LITERAL de pricing.ts:71-81: computar → min(subtotal) → redondear.
 */
export function computeCouponDiscount(math: CouponMath, rawSubtotal: number): number {
  if (rawSubtotal <= 0) return 0
  let amount =
    math.discount_type === "percent"
      ? rawSubtotal * (math.discount_value / 100)
      : math.discount_value
  amount = Math.min(amount, rawSubtotal)
  return Math.round(amount * 100) / 100
}

/** Subconjunto de la fila de `coupons` que las restricciones necesitan. */
export interface CouponRestrictionRow {
  archived_at: string | null
  is_active: boolean
  starts_at: string | null
  ends_at: string | null
  max_redemptions: number | null
  redemption_count: number
  min_subtotal: number | null
}

/**
 * Todo lo de checkCouponRestrictions MENOS el mínimo de subtotal.
 * Se extrae para que la variante con ámbito pueda meter `assigned_mismatch` y
 * `scope_not_matched` entre `exhausted` y `min_subtotal` sin duplicar reglas.
 */
function checkCouponRestrictionsCore(
  row: CouponRestrictionRow,
  now: Date,
): CouponRejectReason | null {
  if (row.archived_at !== null) return "not_found"
  if (!row.is_active) return "inactive"
  if (row.starts_at !== null && new Date(row.starts_at) > now) return "not_started"
  if (row.ends_at !== null && new Date(row.ends_at) <= now) return "expired"
  if (row.max_redemptions !== null && row.redemption_count >= row.max_redemptions) return "exhausted"
  return null
}

/**
 * Reglas de la spec §4/§5, en este ORDEN (los tests lo fijan):
 * archivado→not_found (sin oráculo de existencia), inactive, not_started,
 * expired (ends_at EXCLUSIVO), exhausted (>= tolera overshoot), min_subtotal.
 *
 * DÓNDE VIVE CADA UNA: los cinco primeros chequeos ya NO están en el cuerpo de
 * esta función — se extrajeron a `checkCouponRestrictionsCore` (arriba) para que
 * `checkScopedCouponRestrictions` pueda insertar `assigned_mismatch` y
 * `scope_not_matched` entre `exhausted` y `min_subtotal` sin duplicar reglas.
 * Acá quedó solo `min_subtotal`, medido sobre el número que recibe. El orden y el
 * comportamiento observable son idénticos a los de v1: esta firma es la que los
 * rieles usan HOY en producción y no cambió.
 *
 * A1b (TABC): el mínimo se mide sobre la base DEL CUPÓN, la que no cuenta el
 * alcohol. Los llamadores pasan `couponBase` (ver `couponBaseFromLines` de
 * lib/coupons/alcoholExclusion.ts) cuando la tienen; sin líneas de alcohol esa
 * base ES el `rawSubtotal` de siempre. El parámetro conserva su nombre para no
 * mover la firma.
 *
 * NO chequea: gate del location, teléfono (already_used) ni ancla — eso es de
 * los rieles con DB. `now` es parámetro: el motor no lee el reloj.
 */
export function checkCouponRestrictions(
  row: CouponRestrictionRow,
  rawSubtotal: number,
  now: Date,
): CouponRejectReason | null {
  const core = checkCouponRestrictionsCore(row, now)
  if (core !== null) return core
  if (row.min_subtotal !== null && rawSubtotal < row.min_subtotal) return "min_subtotal"
  return null
}

export interface ScopedRestrictionRow extends CouponRestrictionRow {
  /**
   * `null` = cualquiera lo canjea. Cuando no es null, el formato E.164
   * (`+` + primer dígito 1-9 + 7 a 14 dígitos más) lo GARANTIZA el CHECK
   * `coupons_assigned_phone_e164` de la base (ronda de arreglo 1, hallazgo H1)
   * — este motor no normaliza nada, compara con IGUALDAD EXACTA de strings.
   */
  assigned_phone: string | null
}

/**
 * Restricciones con ámbito y teléfono asignado (spec §6.4).
 *
 * Orden: núcleo v1 (archivado→not_found, inactive, not_started, expired,
 * exhausted) → assigned_mismatch → scope_not_matched → min_subtotal.
 *
 * El ámbito va ANTES del mínimo porque con ámbito el mínimo se mide sobre el
 * subtotal del ámbito (D-9): evaluarlo al revés compararía contra una cifra que
 * todavía no existe.
 *
 * `already_used` y `already_consumed` NO se chequean acá: son de DB (índice
 * único de teléfono y `consumed_at` del ancla) y los hacen los rieles entre
 * `exhausted` y `assigned_mismatch`.
 *
 * `assigned_phone` es ATRIBUCIÓN, no autenticación (D-19): quien tenga el
 * código y el teléfono canjea. El copy no promete lo contrario.
 */
export function checkScopedCouponRestrictions(
  row: ScopedRestrictionRow,
  ctx: { scopedSubtotal: number; scopeMatched: boolean; customerPhone: string | null },
  now: Date,
): CouponRejectReason | null {
  const core = checkCouponRestrictionsCore(row, now)
  if (core !== null) return core

  if (row.assigned_phone !== null && ctx.customerPhone !== row.assigned_phone) {
    return "assigned_mismatch"
  }
  if (!ctx.scopeMatched) return "scope_not_matched"
  if (row.min_subtotal !== null && ctx.scopedSubtotal < row.min_subtotal) return "min_subtotal"
  return null
}

export interface ManualDiscountInput {
  type: CouponDiscountType
  value: number
  /** 'loyalty' = reward pagado con puntos (LoyaltyPanel); 'supervisor' = descuento con PIN. */
  source: "supervisor" | "loyalty"
}

export interface DiscountStackInput {
  rawSubtotal: number
  /**
   * A1b (TABC): base sobre la que se computa el CUPÓN — `rawSubtotal` menos las
   * líneas de alcohol (los cupones nunca rebajan alcohol). Ausente = `rawSubtotal`,
   * byte a byte lo de main. El descuento manual (PIN / lealtad), el tope de la
   * suma y el recorte siguen SIEMPRE sobre `rawSubtotal`: el alcohol sí cuenta
   * para ellos.
   */
  couponBase?: number
  coupon: CouponMath | null
  manual: ManualDiscountInput | null
}

export interface DiscountStackResult {
  couponAmount: number
  manualAmount: number
  totalDiscount: number
  couponClipped: boolean
  manualClipped: boolean
}

/**
 * Stacking de la spec §4: cada descuento se computa sobre rawSubtotal; la suma
 * topa en rawSubtotal. Cuando el tope muerde:
 *  - manual 'supervisor' → ABSORBE EL MANUAL (P1-13): el cupón conserva su
 *    matemática validada (coupon_discount_amount snapshot).
 *  - manual 'loyalty' → SE RECORTA EL CUPÓN (backstop P0-16): un reward ya
 *    pagado con puntos jamás se absorbe. (La UI de v1 impide la combinación;
 *    esto es el backstop server si igual llega.)
 *
 * A1b: el cupón se computa sobre `couponBase ?? rawSubtotal` (sin alcohol). Todo
 * lo demás —manual, tope de la suma, recortes— sigue sobre `rawSubtotal`.
 */
export function applyDiscountStack(input: DiscountStackInput): DiscountStackResult {
  const { rawSubtotal } = input
  const couponRaw = input.coupon
    ? computeCouponDiscount(input.coupon, input.couponBase ?? rawSubtotal)
    : 0
  const manualRaw = input.manual
    ? computeCouponDiscount(
        { discount_type: input.manual.type, discount_value: input.manual.value },
        rawSubtotal,
      )
    : 0

  if (couponRaw + manualRaw <= rawSubtotal) {
    return {
      couponAmount: couponRaw,
      manualAmount: manualRaw,
      totalDiscount: Math.round((couponRaw + manualRaw) * 100) / 100,
      couponClipped: false,
      manualClipped: false,
    }
  }

  if (input.manual?.source === "loyalty") {
    const couponAmount = Math.round(Math.max(0, rawSubtotal - manualRaw) * 100) / 100
    return {
      couponAmount,
      manualAmount: manualRaw,
      totalDiscount: Math.round((couponAmount + manualRaw) * 100) / 100,
      couponClipped: true,
      manualClipped: false,
    }
  }

  const manualAmount = Math.round(Math.max(0, rawSubtotal - couponRaw) * 100) / 100
  return {
    couponAmount: couponRaw,
    manualAmount,
    totalDiscount: Math.round((couponRaw + manualAmount) * 100) / 100,
    couponClipped: false,
    manualClipped: true,
  }
}

/** Una línea del carrito, ya en base card y con sus modificadores incluidos (spec D-5). */
export interface CouponLine {
  item_id: string
  category_id: string | null
  /** Precio de UNA unidad: ítem + sus modificadores. Uniforme dentro de la línea. */
  unit_price: number
  quantity: number
}

/** Ámbito del cupón: unión de categorías e ítems. `null` = toda la orden. */
export interface CouponScope {
  category_ids: string[]
  item_ids: string[]
}

/**
 * MATCHEO CANÓNICO del ámbito sobre UNA línea (spec §6.2 paso 1).
 *
 * Está exportada a propósito: es la única definición de "esta línea cae en el
 * ámbito" en todo el repo, y `selectScopedUnits` (el camino del dinero) la usa.
 * NADIE debe reimplementarla — si el riel que decide `scope_not_matched`
 * matchea distinto del que decide el MONTO (p. ej. olvidando el
 * `line.category_id !== null`), el cliente ve un motivo y paga otra cifra. Para
 * la pregunta de orden completa usá `scopeMatchesLines`, que se apoya en esta.
 */
export function lineMatchesScope(line: CouponLine, scope: CouponScope | null): boolean {
  if (scope === null) return true
  if (scope.item_ids.includes(line.item_id)) return true
  return line.category_id !== null && scope.category_ids.includes(line.category_id)
}

/**
 * ¿El carrito tiene ALGO del ámbito? Es el booleano que el riel le pasa a
 * `checkScopedCouponRestrictions` como `ctx.scopeMatched` para decidir
 * `scope_not_matched` (spec §6.4), y resuelve la ambigüedad del `{0,0}` que
 * `computeScopedCouponDiscount` documenta: un ámbito que no matchea y un
 * carrito vacío dan el mismo retorno numérico, pero motivos distintos.
 *
 * `scope === null` es `true` SIEMPRE, incluso con `lines` vacío: sin ámbito no
 * hay nada que matchear, y `scope_not_matched` sería un motivo mentiroso. Con
 * ámbito, un carrito vacío es `false` — no tiene nada del ámbito, literalmente.
 *
 * Delega en `lineMatchesScope` a propósito: una sola fuente de verdad para el
 * matcheo, la misma que decide el monto (F-5 de la revisión final).
 */
export function scopeMatchesLines(lines: CouponLine[], scope: CouponScope | null): boolean {
  if (scope === null) return true
  return lines.some((line) => lineMatchesScope(line, scope))
}

/**
 * Precios de las unidades que el cupón cubre, de mayor a menor.
 *
 * Los modificadores son uniformes dentro de una línea — el carrito calcula
 * `(basePrice + modPrice) * quantity` (app/api/orders/route.ts:764) — así que
 * expandir `quantity` a unidades de precio uniforme es exacto y D-4 nunca puede
 * cubrir "la unidad equivocada".
 *
 * Desempate por `item_id` ASC: mantiene el orden total y estable, pero no es
 * observable desde el retorno (que solo devuelve precios `number[]`), así que
 * deliberadamente no hay test que lo cubra. La cláusula es higiene defensiva.
 *
 * 🟡 A1b (TABC) — PENDIENTE PARA PR-2b: los cupones con ámbito no están vivos
 * (validateCouponStrict los rechaza), así que este camino todavía NO excluye
 * alcohol. Cuando PR-2b lo encienda tiene que (1) saltar las líneas de alcohol
 * acá (`lineIsCouponAlcohol` de lib/coupons/alcoholExclusion.ts) y (2) medir el
 * mínimo y el tope sobre `couponBase`, igual que el cupón sin ámbito.
 */
export function selectScopedUnits(
  lines: CouponLine[],
  scope: CouponScope | null,
  maxScopeUnits: number | null,
): number[] {
  const units: Array<{ price: number; itemId: string }> = []
  for (const line of lines) {
    if (!lineMatchesScope(line, scope)) continue
    for (let i = 0; i < line.quantity; i++) {
      units.push({ price: line.unit_price, itemId: line.item_id })
    }
  }
  units.sort((a, b) => (b.price - a.price) || (a.itemId < b.itemId ? -1 : a.itemId > b.itemId ? 1 : 0))
  // CLAMP (F-2 de la revisión final): `maxScopeUnits <= 0` = "no cubre ninguna
  // unidad". Sin esta rama, `slice(0, -1)` devuelve TODAS MENOS LA MÁS BARATA —
  // en silencio, y con una cifra que no es la que el ancla dice describir.
  // `coupons` tiene CHECK (> 0), pero el ancla es de donde D-11 recomputa y sus
  // columnas nacieron sin CHECK; la migración de este PR los agrega, y este
  // clamp es el cinturón del lado del motor (hermano del Math.max(0, amount)
  // de computeScopedCouponDiscount).
  const kept =
    maxScopeUnits === null
      ? units
      : maxScopeUnits <= 0
        ? []
        : units.slice(0, maxScopeUnits)
  return kept.map((u) => u.price)
}

/**
 * Subtotal de las unidades cubiertas, NORMALIZADO A CENTAVO (spec D-15).
 *
 * La normalización no es cosmética. v1 nunca la necesitó en el motor porque el
 * subtotal le llegaba ya normalizado por el riel (app/api/orders/route.ts:767,
 * `rawSubtotal = Math.round(rawSubtotal * 100) / 100`, ANTES de comparar el
 * mínimo en la 904). El motor de v2 introduce una suma propia, así que repite
 * esa línea: con los 12 precios reales del menú de Empanada Factory, 427 de
 * 1728 sumas de tres unidades derivan del valor exacto y 182 suman MENOS —
 * `3.65 + 3.65 + 7.10 = 14.399999999999999` rechazaría un mínimo de $14.40.
 *
 * Camino rápido: sin ámbito y sin tope, el resultado es exactamente el
 * `rawSubtotal` que el llamador ya normalizó — cero cambio de comportamiento
 * frente a v1.
 */
export function computeScopedSubtotal(
  lines: CouponLine[],
  scope: CouponScope | null,
  maxScopeUnits: number | null,
  rawSubtotal: number,
): number {
  if (scope === null && maxScopeUnits === null) return rawSubtotal
  const units = selectScopedUnits(lines, scope, maxScopeUnits)
  let sum = 0
  for (const price of units) sum += price
  return Math.round(sum * 100) / 100
}

export interface ScopedCouponMath {
  discount_type: CouponDiscountType
  discount_value: number
  scope: CouponScope | null
  max_scope_units: number | null
  max_discount_amount: number | null
}

/**
 * Descuento del cupón con ámbito y topes (spec §6.2).
 *
 * Secuencia: matchear → expandir y recortar por unidades → sumar y NORMALIZAR →
 * computar → topar en el ámbito → topar en el techo → CLAMP a >= 0 → redondear
 * UNA vez.
 *
 * El paso que cambia frente a v1 es el tope: v1 topaba en el total de la orden,
 * v2 topa en `scopedSubtotal`. Un "$20 fijo en empanadas" ya no puede tocar la
 * bebida.
 *
 * Devuelve también `scopedSubtotal` porque el riel lo necesita para comparar
 * `min_subtotal` (D-9) y para guardar `validated_scoped_subtotal` en el ancla (D-18).
 *
 * AMBIGÜEDAD DEL {0,0}: cuando el ámbito no matchea ninguna línea Y cuando el
 * carrito llega vacío devuelven, ambos, `{ amount: 0, scopedSubtotal: 0 }` —
 * el tipo de retorno no distingue el motivo. Aguas abajo eso decide si el
 * cliente ve "este cupón es solo para Empanadas" o un mensaje genérico; este
 * retorno no lo resuelve por sí solo. La respuesta canónica es
 * `scopeMatchesLines(lines, math.scope)` — NO reimplementar el matcheo (F-5).
 */
export function computeScopedCouponDiscount(
  math: ScopedCouponMath,
  lines: CouponLine[],
  rawSubtotal: number,
): { amount: number; scopedSubtotal: number } {
  const scopedSubtotal = computeScopedSubtotal(
    lines,
    math.scope,
    math.max_scope_units,
    rawSubtotal,
  )
  if (scopedSubtotal <= 0) return { amount: 0, scopedSubtotal: 0 }

  let amount =
    math.discount_type === "percent"
      ? scopedSubtotal * (math.discount_value / 100)
      : math.discount_value

  amount = Math.min(amount, scopedSubtotal)
  if (math.max_discount_amount !== null) {
    amount = Math.min(amount, math.max_discount_amount)
  }
  // H2: un max_discount_amount cargado en negativo no puede convertir el
  // descuento en un cargo. Desde F-2 el CHECK existe en las DOS tablas
  // (`coupons` y `coupon_validations`), así que esto es cinturón sobre
  // tirantes — pero el motor es puro y no puede asumir su llamador. v1 nunca
  // necesitó este clamp porque su único tope era rawSubtotal, que
  // checkCouponRestrictions ya garantiza >= 0.
  amount = Math.max(0, amount)
  return { amount: Math.round(amount * 100) / 100, scopedSubtotal }
}
